import { api } from '@/lib/api/client'
import {
  normalizeDistributor,
  normalizeDistributorCredential,
} from '@/lib/distributor-normalize'
import type {
  CreateDistributorInput,
  CreateDistributorResponse,
  FetchDistributorAffiliatesResponse,
  FetchDistributorBalanceTransactionsResponse,
  FetchDistributorPaymentsParams,
  FetchDistributorPaymentsResponse,
  FetchDistributorsResponse,
  SearchPlatformUserForDistributorResponse,
  UpdateDistributorInput,
} from '@/types/distributors'

export async function fetchDistributors(): Promise<FetchDistributorsResponse> {
  const res = await api.get<FetchDistributorsResponse>('/api/institution/distributors', {
    requireAuth: true,
  })
  return {
    ...res,
    distributors: res.distributors.map(normalizeDistributor),
  }
}

export async function searchPlatformUserForDistributor(input: {
  email?: string
  phone?: string
}): Promise<SearchPlatformUserForDistributorResponse> {
  return api.post<SearchPlatformUserForDistributorResponse>(
    '/api/institution/distributors/users/search',
    input,
    { requireAuth: true }
  )
}

export async function createDistributor(
  input: CreateDistributorInput
): Promise<CreateDistributorResponse> {
  const res = await api.post<CreateDistributorResponse>('/api/institution/distributors', input, {
    requireAuth: true,
  })
  return { distributor: normalizeDistributorCredential(res.distributor) }
}

export async function updateDistributor(
  id: number,
  input: UpdateDistributorInput
): Promise<CreateDistributorResponse> {
  const res = await api.put<CreateDistributorResponse>(
    `/api/institution/distributors/${id}`,
    input,
    { requireAuth: true }
  )
  return { distributor: normalizeDistributorCredential(res.distributor) }
}

export async function fetchDistributorPayments(
  params: FetchDistributorPaymentsParams = {}
): Promise<FetchDistributorPaymentsResponse> {
  const searchParams = new URLSearchParams()
  if (params.distributor_id != null) {
    searchParams.set('distributor_id', String(params.distributor_id))
  }
  if (params.campaign_id != null) searchParams.set('campaign_id', String(params.campaign_id))
  if (params.page != null) searchParams.set('page', String(params.page))
  if (params.per_page != null) searchParams.set('per_page', String(params.per_page))

  const query = searchParams.toString()
  return api.get<FetchDistributorPaymentsResponse>(
    `/api/institution/distributors/payments${query ? `?${query}` : ''}`,
    { requireAuth: true }
  )
}

export async function fetchDistributorBalanceTransactions(
  distributorId: number,
  params: { page?: number; per_page?: number } = {}
): Promise<FetchDistributorBalanceTransactionsResponse> {
  const searchParams = new URLSearchParams()
  if (params.page != null) searchParams.set('page', String(params.page))
  if (params.per_page != null) searchParams.set('per_page', String(params.per_page))
  const q = searchParams.toString()
  return api.get<FetchDistributorBalanceTransactionsResponse>(
    `/api/institution/distributors/${distributorId}/balance-transactions${q ? `?${q}` : ''}`,
    { requireAuth: true }
  )
}

export async function recordDistributorPayout(
  distributorId: number,
  input: { amount: number; note?: string | null }
): Promise<CreateDistributorResponse> {
  return api.post<CreateDistributorResponse>(
    `/api/institution/distributors/${distributorId}/payout`,
    input,
    { requireAuth: true }
  )
}

export async function fetchDistributorAffiliates(
  distributorId: number
): Promise<FetchDistributorAffiliatesResponse> {
  return api.get<FetchDistributorAffiliatesResponse>(
    `/api/institution/distributors/${distributorId}/affiliates`,
    { requireAuth: true }
  )
}

export interface CreateDistributorAffiliateRegistrationInput {
  platform_user_id: number
  commission_percent: number
  all_campaigns: boolean
  campaigns?: Array<{ campaign_id: number; commission_percent: number }>
  payout_method?: 'doey' | 'manual'
  is_active?: boolean
}

export async function createDistributorAffiliateRegistration(
  distributorId: number,
  input: CreateDistributorAffiliateRegistrationInput
): Promise<FetchDistributorAffiliatesResponse> {
  return api.post<FetchDistributorAffiliatesResponse>(
    `/api/institution/distributors/${distributorId}/affiliates`,
    input,
    { requireAuth: true }
  )
}
